/**
 * Campaign Type Constants
 * Centralizes all campaign type definitions used across the frontend
 * IMPORTANT: Keep in sync with backend (portal-api/src/utils/constants/campaign-types.ts)
 */

export const CAMPAIGN_TYPES = {
  PRO012: 'PRO012',
  PRO013: 'PRO013',
  PRO014: 'PRO014',
  PRO017: 'PRO017',
} as const;

export type CampaignType = (typeof CAMPAIGN_TYPES)[keyof typeof CAMPAIGN_TYPES];

/**
 * LEGACY SUPPORT: Export as enum for backward compatibility
 * @deprecated Use CAMPAIGN_TYPES instead
 */
export enum CampaignTypeEnum {
  PRO012 = 'PRO012',
  PRO013 = 'PRO013',
  PRO014 = 'PRO014',
  PRO017 = 'PRO017',
}

/**
 * Campaign Visual Status Constants
 */
export const CAMPAIGN_VISUAL_STATUS = {
  ACTIVE: 'ACTIVE',
  FUTURE: 'FUTURE',
  EXPIRED: 'EXPIRED',
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  REJECTED: 'REJECTED',
} as const;

export type CampaignVisualStatus = (typeof CAMPAIGN_VISUAL_STATUS)[keyof typeof CAMPAIGN_VISUAL_STATUS];

/**
 * Propagation Store Status Constants
 * Status lifecycle: PENDING → IN_PROGRESS → SUCCESS/FAILED
 */
export const PROPAGATION_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;

export type PropagationStoreStatus = (typeof PROPAGATION_STATUS)[keyof typeof PROPAGATION_STATUS];

/**
 * Propagation Overall Status Constants
 * Aggregated status for entire propagation
 */
export const PROPAGATION_OVERALL_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  PARTIAL_FAILURE: 'PARTIAL_FAILURE',
} as const;

export type PropagationOverallStatus = (typeof PROPAGATION_OVERALL_STATUS)[keyof typeof PROPAGATION_OVERALL_STATUS];

/**
 * Campaign type descriptions for UI display
 */
export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  [CAMPAIGN_TYPES.PRO012]: 'Combo Legado',
  [CAMPAIGN_TYPES.PRO013]: 'Leve X Pague Y',
  [CAMPAIGN_TYPES.PRO014]: 'Lista de Itens',
  [CAMPAIGN_TYPES.PRO017]: 'Desconto por Lista',
};

/**
 * Campaign type colors for badges/chips
 */
export const CAMPAIGN_TYPE_COLORS: Record<
  CampaignType,
  'gray' | 'blue' | 'green' | 'purple'
> = {
  [CAMPAIGN_TYPES.PRO012]: 'gray',
  [CAMPAIGN_TYPES.PRO013]: 'purple',
  [CAMPAIGN_TYPES.PRO014]: 'blue',
  [CAMPAIGN_TYPES.PRO017]: 'green',
};

/**
 * Validate if a string is a valid campaign type
 */
export function isValidCampaignType(type: string): type is CampaignType {
  return Object.values(CAMPAIGN_TYPES).includes(type as CampaignType);
}

/**
 * Get campaign type label (fallback to raw type if not found)
 */
export function getCampaignTypeLabel(type: string): string {
  if (isValidCampaignType(type)) {
    return CAMPAIGN_TYPE_LABELS[type];
  }
  return type; // Fallback for unknown types
}

/**
 * Get campaign type color (fallback to blue if not found)
 */
export function getCampaignTypeColor(
  type: string,
): 'gray' | 'blue' | 'green' | 'purple' {
  if (isValidCampaignType(type)) {
    return CAMPAIGN_TYPE_COLORS[type];
  }
  return 'blue'; // Default fallback
}

/**
 * Campaign Status Labels (pt-BR)
 */
export const CAMPAIGN_STATUS_LABELS: Record<CampaignVisualStatus, string> = {
  [CAMPAIGN_VISUAL_STATUS.ACTIVE]: 'Ativa',
  [CAMPAIGN_VISUAL_STATUS.FUTURE]: 'Futura',
  [CAMPAIGN_VISUAL_STATUS.EXPIRED]: 'Expirada',
  [CAMPAIGN_VISUAL_STATUS.PENDING]: 'Pendente',
  [CAMPAIGN_VISUAL_STATUS.COMPLETED]: 'Concluída',
  [CAMPAIGN_VISUAL_STATUS.CANCELLED]: 'Cancelada',
  [CAMPAIGN_VISUAL_STATUS.REJECTED]: 'Rejeitada',
};

/**
 * Campaign Status Colors for badges
 */
export const CAMPAIGN_STATUS_COLORS: Record<CampaignVisualStatus, string> = {
  [CAMPAIGN_VISUAL_STATUS.ACTIVE]: 'bg-green-100 text-green-800 border-green-200',
  [CAMPAIGN_VISUAL_STATUS.FUTURE]: 'bg-blue-100 text-blue-800 border-blue-200',
  [CAMPAIGN_VISUAL_STATUS.EXPIRED]: 'bg-gray-100 text-gray-800 border-gray-200',
  [CAMPAIGN_VISUAL_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  [CAMPAIGN_VISUAL_STATUS.COMPLETED]: 'bg-gray-100 text-gray-800 border-gray-200',
  [CAMPAIGN_VISUAL_STATUS.CANCELLED]: 'bg-red-100 text-red-800 border-red-200',
  [CAMPAIGN_VISUAL_STATUS.REJECTED]: 'bg-red-100 text-red-800 border-red-200',
};

/**
 * Campaign Permissions by Type
 */
export const CAMPAIGN_PERMISSIONS = {
  [CAMPAIGN_TYPES.PRO012]: {
    canEdit: false,
    canDelete: false,
    canCreate: false,
    readonly: true,
  },
  [CAMPAIGN_TYPES.PRO013]: {
    canEdit: true,
    canDelete: true,
    canCreate: true,
    readonly: false,
  },
  [CAMPAIGN_TYPES.PRO014]: {
    canEdit: true,
    canDelete: true,
    canCreate: true,
    readonly: false,
  },
  [CAMPAIGN_TYPES.PRO017]: {
    canEdit: true,
    canDelete: true,
    canCreate: true,
    readonly: false,
  },
} as const;

/**
 * Helper: Get campaign status label
 */
export function getCampaignStatusLabel(status: CampaignVisualStatus): string {
  return CAMPAIGN_STATUS_LABELS[status] || status;
}

/**
 * Helper: Check if campaign can be edited
 */
export function canEditCampaign(type: CampaignType): boolean {
  return CAMPAIGN_PERMISSIONS[type].canEdit;
}

/**
 * Helper: Check if campaign can be deleted
 */
export function canDeleteCampaign(type: CampaignType): boolean {
  return CAMPAIGN_PERMISSIONS[type].canDelete;
}

/**
 * Helper: Check if campaign can be created
 */
export function canCreateCampaign(type: CampaignType): boolean {
  return CAMPAIGN_PERMISSIONS[type].canCreate;
}
